import { useEffect, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import { toast } from 'react-toastify'
import { DEVICE_COMPLAINT_CLOSED_STATUS, DEVICE_COMPLAINT_STATUSES } from 'constants/deviceComplaints'
import { useUpdateDeviceComplaintStatusMutation } from 'store/services/deviceComplaints'
import { DeviceComplaintDto, UpdateDeviceComplaintStatusDto } from 'store/types'

interface DeviceComplaintStatusModalProps {
  complaint: DeviceComplaintDto | null
  onClose: () => void
}

export const DeviceComplaintStatusModal = ({ complaint, onClose }: DeviceComplaintStatusModalProps) => {
  const [status, setStatus] = useState('')
  const [notes, setNotes] = useState('')
  const [updateStatus, { isLoading }] = useUpdateDeviceComplaintStatusMutation()

  useEffect(() => {
    setStatus(complaint?.status ?? '')
    setNotes(complaint?.statusNotes ?? '')
  }, [complaint])

  const notesRequired = status === DEVICE_COMPLAINT_CLOSED_STATUS

  const handleSave = async () => {
    if (!complaint) return

    try {
      await updateStatus({
        id: complaint.id,
        status: status as UpdateDeviceComplaintStatusDto['status'],
        notes,
      }).unwrap()
      toast.success('Complaint status updated.')
      onClose()
    } catch (err: any) {
      toast.error(err?.data?.message || 'Could not update the complaint status.')
    }
  }

  return (
    <Modal show={!!complaint} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Update Complaint Status</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="form-group">
          <label htmlFor="complaint-status">Status</label>
          <select
            id="complaint-status"
            className="form-control"
            value={status}
            onChange={(e) => setStatus(e.target.value)}>
            {DEVICE_COMPLAINT_STATUSES.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="complaint-status-notes">{notesRequired ? 'Resolution Notes *' : 'Notes'}</label>
          <textarea
            id="complaint-status-notes"
            className="form-control"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isLoading || (notesRequired && !notes.trim())}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
